import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import ActionsAPI from 'services/ActionsAPI'
import { CRITICALITY_LEVELS } from 'features/compliance/obligationConstants'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import HubScopeFilter from 'features/taskManagement/components/HubScopeFilter'
import SearchableSelect from 'components/SearchableSelect'
import BulkConvertDialog from 'features/actions/components/BulkConvertDialog'
import OldSuggestionsCleanup from './OldSuggestionsCleanup'

const SEV_META = Object.fromEntries(CRITICALITY_LEVELS.map(c => [c.key, c]))
const sevColour = s => SEV_META[s]?.colour || '#6e6e78'
const sevLabel = s => SEV_META[s]?.label || (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—')
const PER_PAGE = 50

const identifiedMeta = (dateStr) => {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const day = new Date(d)
  day.setHours(0, 0, 0, 0)
  const diffDays = Math.round((today - day) / 86400000)
  const label = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const sub = diffDays <= 0 ? 'today' : diffDays === 1 ? 'yesterday' : `${diffDays}d ago`
  return { label, sub }
}

const ExtractedRemedialsReview = ({ bpId = null, defaultExpanded = false, onApproved = null, pageMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const bpFilter = bpId || searchParams.get('bp') || null
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(null)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(pageMode || defaultExpanded || searchParams.get('review') === '1')
  const [selected, setSelected] = useState(() => new Set())
  const [dismissedIds, setDismissedIds] = useState(() => new Set())
  const [insightFile, setInsightFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [sort, setSort] = useState('severity_desc')
  const [groupByDoc, setGroupByDoc] = useState(true)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())
  const [grandTotal, setGrandTotal] = useState(null)
  const [reachedEnd, setReachedEnd] = useState(false)
  const [convertIds, setConvertIds] = useState(null)
  const seen = useRef(new Set())

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => clearTimeout(t)
  }, [search])

  const load = useCallback(p => {
    setLoading(true)
    ActionsAPI.list({
      'filter[status]': 'pending_review',
      'filter[source]': 'guru',
      ...(bpFilter ? { 'filter[building_passport_id]': bpFilter } : {}),
      ...(debouncedSearch ? { 'filter[search]': debouncedSearch } : {}),
      ...(severityFilter ? { 'filter[severity]': severityFilter } : {}),
      page: p,
      per_page: PER_PAGE,
      sort,
    })
      .then(res => {
        const incoming = res.data?.data || []
        const meta = res.data?.meta || {}
        const t = meta.total ?? incoming.length
        setTotal(t)
        if (!debouncedSearch && !severityFilter) setGrandTotal(t)
        setLastPage(meta.lastPage ?? 1)
        setPage(meta.currentPage ?? p)
        if (p <= 1) {
          seen.current = new Set(incoming.map(i => i.id))
          setReachedEnd(false)
          setItems(incoming)
          return
        }
        const fresh = incoming.filter(i => !seen.current.has(i.id))
        fresh.forEach(i => seen.current.add(i.id))
        setReachedEnd(fresh.length === 0)
        setItems(prev => [...prev, ...fresh])
      })
      .catch(() => {
        setTotal(t => (t === null ? 0 : t))
        setGrandTotal(g => (g === null ? 0 : g))
      })
      .finally(() => setLoading(false))
  }, [bpFilter, debouncedSearch, severityFilter, sort])

  useEffect(() => { load(1) }, [load])

  const remove = useCallback(ids => {
    const idset = new Set(ids)
    setItems(prev => prev.filter(i => !idset.has(i.id)))
    setTotal(t => Math.max(0, (t || 0) - ids.length))
    setGrandTotal(g => (g === null ? g : Math.max(0, g - ids.length)))
    setSelected(prev => {
      const n = new Set(prev)
      ids.forEach(id => n.delete(id))
      return n
    })
  }, [])

  const toggleSelect = id => setSelected(prev => {
    const n = new Set(prev)
    n.has(id) ? n.delete(id) : n.add(id)
    return n
  })

  const allLoadedSelected = items.length > 0 && items.every(i => selected.has(i.id))
  const toggleSelectAll = () => setSelected(() => (allLoadedSelected ? new Set() : new Set(items.map(i => i.id))))

  const openInsight = item => {
    if (!item?.sourceFileId) return
    const buildingPassportId = item?.buildingPassport?.id || bpId
    if (!buildingPassportId) return
    setInsightFile({
      id: item.sourceFileId,
      displayName: item.sourceFile?.displayName,
      buildingPassportId,
      buildingPassportName: item.buildingPassport?.name || null,
    })
  }

  const bulkApprove = async ids => {
    if (!ids.length || busy) return
    setBusy(true)
    try {
      const chunks = []
      for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200))
      const results = await Promise.allSettled(chunks.map(chunk => ActionsAPI.bulkApprove({ ids: chunk })))
      const okIds = results.flatMap(r => (r.status === 'fulfilled' ? (r.value?.data?.data?.approvedIds || []) : []))
      if (okIds.length) {
        remove(okIds)
        onApproved?.()
        toast.success(`${okIds.length} remedial${okIds.length > 1 ? 's' : ''} approved. Now active in your remedials.`)
      }
      if (okIds.length < ids.length) {
        toast.error(`Couldn't approve ${ids.length - okIds.length}. Please try again.`)
      }
    } finally {
      setBusy(false)
    }
  }

  const bulkDismiss = async ids => {
    if (!ids.length || busy) return
    setBusy(true)
    try {
      const res = await ActionsAPI.bulkDismiss({ ids, reason: 'Not a real issue' })
      const done = res?.data?.data?.dismissedIds || ids
      remove(done)
      toast.success(({ closeToast }) => (
        <ToastLine>
          <span>{done.length} AI find{done.length > 1 ? 's' : ''} removed as not a real issue.</span>
          <ToastUndo type='button' onClick={async () => {
            closeToast?.()
            await Promise.allSettled(done.map(id => ActionsAPI.undismiss(id)))
            load(1)
            toast.success('Put back for review.')
          }}>Undo</ToastUndo>
        </ToastLine>
      ), { autoClose: 10000 })
    } catch (e) {
      toast.error('Those AI finds could not be removed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const dismissOne = async item => {
    if (busy) return
    setBusy(true)
    try {
      await ActionsAPI.dismiss(item.id, {})
      setDismissedIds(prev => new Set(prev).add(item.id))
      setSelected(prev => {
        const n = new Set(prev)
        n.delete(item.id)
        return n
      })
      toast.success('Removed as not a real issue. You can undo this.')
    } catch (e) {
      toast.error('This AI find could not be removed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const undismissOne = async item => {
    if (busy) return
    setBusy(true)
    try {
      await ActionsAPI.undismiss(item.id)
      setDismissedIds(prev => {
        const n = new Set(prev)
        n.delete(item.id)
        return n
      })
      toast.success('Remedial restored. Back for review.')
    } catch (e) {
      toast.error('Could not undo that.')
    } finally {
      setBusy(false)
    }
  }

  if (total === null) {
    return pageMode
      ? <Wrap><ReviewSkeleton>{[0, 1, 2, 3, 4, 5].map(i => <SkelRow key={i} />)}</ReviewSkeleton></Wrap>
      : null
  }
  if (grandTotal === 0) {
    return pageMode
      ? <Wrap><EmptyReview>No remedials are waiting for review right now.</EmptyReview></Wrap>
      : null
  }

  const selectedIds = [...selected]
  const bannerTotal = grandTotal ?? total
  const hasFilter = !!debouncedSearch || !!severityFilter

  const toggleGroup = key => setCollapsedGroups(prev => {
    const n = new Set(prev)
    n.has(key) ? n.delete(key) : n.add(key)
    return n
  })

  const groups = groupByDoc
    ? Array.from(items.reduce((map, item) => {
        const key = item.sourceFile?.id || 'none'
        if (!map.has(key)) map.set(key, { key, name: item.sourceFile?.displayName || 'No document', rows: [] })
        map.get(key).rows.push(item)
        return map
      }, new Map()).values())
    : null

  const firstRowId = groups
    ? groups.find(g => !collapsedGroups.has(g.key) && g.rows.length)?.rows[0]?.id
    : items[0]?.id

  const renderRow = item => {
    const isDismissed = dismissedIds.has(item.id)
    const isFirst = item.id === firstRowId
    return (
      <Tr key={item.id} $sel={selected.has(item.id)} $dismissed={isDismissed}>
        <Td $check>
          <Check type='checkbox' checked={selected.has(item.id)} disabled={isDismissed}
            onChange={() => toggleSelect(item.id)} aria-label='Select remedial' />
        </Td>
        <Td>
          <SevChip $c={sevColour(item.severity)}>{sevLabel(item.severity)}</SevChip>
        </Td>
        <Td>
          {(() => {
            const d = identifiedMeta(item.createdAt)
            return d ? (
              <IdDate>
                <b>{d.label}</b>
                <small>{d.sub}</small>
              </IdDate>
            ) : <Muted>—</Muted>
          })()}
        </Td>
        <Td>
          <ItemTitle $dismissed={isDismissed}>{item.title || 'Untitled remedial'}</ItemTitle>
          {item.description && <ItemDesc>{item.description}</ItemDesc>}
        </Td>
        <Td>
          {item.sourceFile?.displayName ? (
            item.sourceFile.deleted ? (
              <DocMuted title={`${item.sourceFile.displayName} (deleted)`}>{item.sourceFile.displayName} (deleted)</DocMuted>
            ) : (
              <DocLink type='button' onClick={() => openInsight(item)} title={item.sourceFile.displayName}>
                <DescriptionRoundedIcon style={{ fontSize: 14, flexShrink: 0 }} />
                <DocName>{item.sourceFile.displayName}</DocName>
              </DocLink>
            )
          ) : <Muted>—</Muted>}
        </Td>
        <Td>{item.buildingPassport?.name || <Muted>—</Muted>}</Td>
        <Td $right>
          {isDismissed ? (
            <RowActions data-tour-id={isFirst ? 'remedialsReview.first-actions' : undefined}>
              <DismissedInline>Not a real issue</DismissedInline>
              <UndoBtn disabled={busy} onClick={() => undismissOne(item)} title='Undo. Put this remedial back for review'>
                Undo
              </UndoBtn>
            </RowActions>
          ) : (
            <RowActions data-tour-id={isFirst ? 'remedialsReview.first-actions' : undefined}>
              <RowBtn $primary disabled={busy} onClick={() => setConvertIds([item.id])} title='Approve and create task. Confirms this is a real problem and plans the work to fix it, in one step.'>
                Approve and create task
              </RowBtn>
              <RowBtn disabled={busy} onClick={() => bulkApprove([item.id])} title='Approve. Confirms this is a real remedial and adds it to your active remedials. You can create a task later.'>
                <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> Approve
              </RowBtn>
              <RowBtn disabled={busy} onClick={() => dismissOne(item)} title="Not a real issue. Removes this AI find because it is not a real problem. You can undo it.">
                Not a real issue
              </RowBtn>
            </RowActions>
          )}
        </Td>
      </Tr>
    )
  }

  return (
    <Wrap>
      <Banner as={pageMode ? 'div' : 'button'} onClick={pageMode ? undefined : () => setExpanded(e => !e)} $expanded={expanded}>
        <BannerIcon><AutoAwesomeRoundedIcon style={{ fontSize: 20 }} /></BannerIcon>
        <BannerText>
          <BannerTitle>{bannerTotal} remedial{bannerTotal !== 1 ? 's' : ''} found in your documents</BannerTitle>
          <BannerSub>Our AI flagged these from your uploaded documents. Approve the real ones to add them to your active remedials. Mark the rest as not a real issue.</BannerSub>
        </BannerText>
        {!pageMode && <Chevron $open={expanded}><ExpandMoreRoundedIcon /></Chevron>}
      </Banner>

      {expanded && (
        <Body>
          <OldSuggestionsCleanup bpId={bpId} onChanged={() => load(1)} />
          <Controls>
            <SearchInput
              type='search'
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Search title or description…'
              aria-label='Search remedials'
            />
            <HubScopeFilter
              bpOnly
              lockedBpId={bpId}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
            <SevFilters>
              <SevChipBtn type='button' $active={severityFilter === ''} onClick={() => setSeverityFilter('')}>All</SevChipBtn>
              {CRITICALITY_LEVELS.map(c => (
                <SevChipBtn key={c.key} type='button' $active={severityFilter === c.key} $c={c.colour}
                  onClick={() => setSeverityFilter(severityFilter === c.key ? '' : c.key)}>
                  {c.label}
                </SevChipBtn>
              ))}
            </SevFilters>
            <SortSelect
              value={sort}
              onChange={v => setSort(v)}
              options={[
                { value: 'severity_desc', label: 'Highest severity' },
                { value: 'created_desc', label: 'Newest first' },
                { value: 'created_asc', label: 'Oldest first' },
              ]}
              searchable={false}
              ariaLabel='Sort remedials'
            />
            <GroupToggle type='button' $on={groupByDoc} onClick={() => setGroupByDoc(v => !v)} title='Group these remedials by the document they came from'>
              <DescriptionRoundedIcon style={{ fontSize: 15 }} /> Group by document
            </GroupToggle>
          </Controls>

          {selectedIds.length > 0 && (
            <SelectionBar>
              <SelCount>{selectedIds.length} selected</SelCount>
              <SelActions>
                <PrimaryBtn disabled={busy || selectedIds.length > 100} onClick={() => setConvertIds(selectedIds)}
                  title={selectedIds.length > 100 ? 'Choose 100 or fewer to create tasks in one go' : 'Approve these and create a task for each one'}>
                  Approve and create {selectedIds.length === 1 ? 'task' : 'tasks'}
                </PrimaryBtn>
                <GhostBtn disabled={busy} onClick={() => bulkApprove(selectedIds)}>
                  <AssignmentTurnedInRoundedIcon style={{ fontSize: 16 }} /> Approve
                </GhostBtn>
                <GhostBtn disabled={busy} onClick={() => bulkDismiss(selectedIds)}>
                  <BlockRoundedIcon style={{ fontSize: 16 }} /> Not a real issue
                </GhostBtn>
                <ClearSel onClick={() => setSelected(new Set())}>Clear</ClearSel>
              </SelActions>
            </SelectionBar>
          )}

          <TableScroll>
            <Table>
              <colgroup>
                <col style={{ width: 44 }} />
                <col style={{ width: 108 }} />
                <col style={{ width: 140 }} />
                <col />
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 260 }} />
              </colgroup>
              <thead>
                <tr>
                  <Th $check>
                    <Check type='checkbox' checked={allLoadedSelected} onChange={toggleSelectAll} aria-label='Select all loaded remedials' />
                  </Th>
                  <Th>Severity</Th>
                  <Th>Identified</Th>
                  <Th>Remedial</Th>
                  <Th>Document</Th>
                  <Th>Building</Th>
                  <Th $right>Actions</Th>
                </tr>
              </thead>
              <tbody data-tour-id='remedialsReview.list'>
                {items.length === 0 && !loading && (
                  <tr><EmptyCell colSpan={7}>{hasFilter ? 'No remedials match your filters.' : 'No remedials to review.'}</EmptyCell></tr>
                )}
                {groupByDoc
                  ? groups.map(g => {
                      const open = !collapsedGroups.has(g.key)
                      return (
                        <React.Fragment key={g.key}>
                          <tr>
                            <GroupCell colSpan={7} onClick={() => toggleGroup(g.key)}>
                              <GroupInner>
                                <GroupChevron $open={open}><ExpandMoreRoundedIcon style={{ fontSize: 18 }} /></GroupChevron>
                                <DescriptionRoundedIcon style={{ fontSize: 15, flexShrink: 0, color: '#64748b' }} />
                                <GroupName>{g.name}</GroupName>
                                <GroupCount>{g.rows.length}</GroupCount>
                              </GroupInner>
                            </GroupCell>
                          </tr>
                          {open && g.rows.map(renderRow)}
                        </React.Fragment>
                      )
                    })
                  : items.map(renderRow)}
              </tbody>
            </Table>
          </TableScroll>

          <InfiniteScrollSentinel
            hasMore={page < lastPage && !reachedEnd}
            loading={loading}
            onLoadMore={() => load(page + 1)}
          />

          <FootCount>Showing {items.length} of {total}{hasFilter ? ' matching' : ''}</FootCount>
        </Body>
      )}

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => { setInsightFile(null); load(1); onApproved?.() }}
          onUpdate={() => { load(1); onApproved?.() }}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
      <BulkConvertDialog
        ids={convertIds}
        open={!!convertIds}
        approving
        onClose={() => setConvertIds(null)}
        onConverted={() => {
          const ids = convertIds || []
          setConvertIds(null)
          remove(ids)
          onApproved?.()
        }}
      />
    </Wrap>
  )
}

export default ExtractedRemedialsReview

const Wrap = styled.div`
  margin-bottom: 18px;
`

const ReviewSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
`

const SkelRow = styled.div`
  height: 52px;
  border-radius: 10px;
  background: #eef0f4;
  animation: rmdlPulse 1.4s ease-in-out infinite;
  @keyframes rmdlPulse {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
  }
`

const EmptyReview = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #6e6e78;
  font-size: 15px;
`

const Banner = styled.button`
  font: inherit;
  width: 100%;
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #ececec;
  border-left: 4px solid ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ $expanded }) => ($expanded ? '12px 12px 0 0' : '12px')};
  cursor: pointer;
  text-align: left;
  transition: background 150ms ease;
  &:hover { background: #fafbfc; }
`

const BannerIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; flex-shrink: 0;
  border-radius: 9px;
  background: ${({ theme }) => `${theme.colors.secondary}1A`};
  color: ${({ theme }) => theme.colors.secondary};
`

const BannerText = styled.div` flex: 1; min-width: 0; `

const BannerTitle = styled.div`
  font-size: 14px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const BannerSub = styled.div`
  font-size: 12.5px; line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey[800] || '#6e6e78'};
  margin-top: 2px;
`

const Chevron = styled.span`
  display: inline-flex; color: #94a3b8;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 200ms ease;
`

const Body = styled.div`
  border: 1px solid #ececec;
  border-top: none;
  border-radius: 0 0 12px 12px;
  padding: 12px;
  display: flex; flex-direction: column; gap: 10px;
  background: #fff;
`

const Controls = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
`

const SearchInput = styled.input`
  flex: 1; min-width: 220px;
  padding: 8px 13px;
  font: inherit; font-size: 13px;
  border: 1px solid #d9dbe1; border-radius: 999px;
  color: ${({ theme }) => theme.colors.haiti};
  outline: none;
  &::placeholder { color: #9aa0ab; }
  &:focus { border-color: ${({ theme }) => theme.colors.secondary}; }
`

const SevFilters = styled.div` display: flex; gap: 6px; flex-wrap: wrap; `

const SevChipBtn = styled.button`
  font: inherit;
  padding: 6px 12px;
  font-size: 12px; font-weight: 700;
  border-radius: 999px; cursor: pointer;
  border: 1px solid ${({ $active, $c }) => ($active ? ($c || '#0c1229') : '#e4e4e7')};
  background: ${({ $active, $c }) => ($active ? ($c ? `${$c}1a` : '#f0f0f2') : '#fff')};
  color: ${({ $active, $c, theme }) => ($active ? ($c || theme.colors.haiti) : '#6e6e78')};
  &:hover { border-color: ${({ $c }) => $c || '#cbd5e1'}; }
`

const SelectionBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 8px 12px;
  background: ${({ theme }) => `${theme.colors.secondary}12`};
  border: 1px solid ${({ theme }) => `${theme.colors.secondary}40`};
  border-radius: 10px;
`

const SelCount = styled.span`
  font-size: 12.5px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const SelActions = styled.div` display: flex; align-items: center; gap: 8px; `

const PrimaryBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff; border: none; border-radius: 999px;
  min-height: 40px;
  padding: 6px 13px; font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const GhostBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: #fff; color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid #e4e4e7; border-radius: 999px;
  min-height: 40px;
  padding: 6px 13px; font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.lighterGrey}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const ClearSel = styled.button`
  font: inherit;
  background: none; border: none; cursor: pointer;
  font-size: 12px; font-weight: 700; color: ${({ theme }) => theme.colors.secondary};
  &:hover { text-decoration: underline; }
`

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #f0f0f2;
  border-radius: 10px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 820px;
  font-size: 14.5px;
`

const Th = styled.th`
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 13px 16px;
  font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em;
  color: #8a8f9c;
  background: #fafbfc;
  border-bottom: 1px solid #ececec;
  white-space: nowrap;
  ${({ $check }) => $check && 'width: 44px;'}
`

const Tr = styled.tr`
  background: ${({ $sel, $dismissed, theme }) => ($dismissed ? '#f7f7f9' : $sel ? `${theme.colors.secondary}0d` : 'transparent')};
  opacity: ${({ $dismissed }) => ($dismissed ? 0.72 : 1)};
  &:hover { background: ${({ $sel, $dismissed, theme }) => ($dismissed ? '#f7f7f9' : $sel ? `${theme.colors.secondary}14` : '#fafbfc')}; }
`

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #f2f2f4;
  vertical-align: top;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  ${({ $check }) => $check && 'width: 44px;'}
`

const EmptyCell = styled.td`
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8; font-size: 13px;
`

const Check = styled.input` cursor: pointer; margin: 0; `

const SevChip = styled.span`
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12.5px; font-weight: 800;
  white-space: nowrap;
  color: ${({ $c }) => $c};
  background: ${({ $c }) => `${$c}1a`};
`

const IdDate = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.25;
  font-size: 13px;
  color: #334155;
  b { font-weight: 700; }
  small { font-size: 11px; font-weight: 500; color: #94a3b8; }
`

const SortSelect = styled(SearchableSelect)`
  width: 170px;
`

const GroupToggle = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $on }) => ($on ? '#33becc' : '#e2e8f0')};
  background: ${({ $on }) => ($on ? 'rgba(51,190,204,0.1)' : '#fff')};
  color: ${({ $on }) => ($on ? '#0f7a86' : '#475569')};
  &:hover { border-color: ${({ $on }) => ($on ? '#33becc' : '#cbd5e1')}; }
`

const GroupCell = styled.td`
  padding: 11px 16px;
  background: #f4f6f8;
  border-top: 1px solid #e6e9ee;
  border-bottom: 1px solid #e6e9ee;
  cursor: pointer;
  user-select: none;
  &:hover { background: #eef1f4; }
`

const GroupInner = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 700; color: #2b2f3a;
`

const GroupChevron = styled.span`
  display: inline-flex;
  color: #64748b;
  transition: transform 0.15s;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
`

const GroupName = styled.span`
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;
`

const GroupCount = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 20px; padding: 0 7px;
  border-radius: 999px;
  background: #e2e8f0; color: #475569;
  font-size: 12px; font-weight: 700;
`

const ItemTitle = styled.div`
  font-size: 15px; font-weight: 700; line-height: 1.4;
  color: ${({ $dismissed, theme }) => ($dismissed ? '#9aa0ab' : theme.colors.haiti)};
  text-decoration: ${({ $dismissed }) => ($dismissed ? 'line-through' : 'none')};
`

const ItemDesc = styled.div`
  margin-top: 4px;
  font-size: 13px; line-height: 1.5;
  color: #6e6e78;
  max-width: 620px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`

const DocLink = styled.button`
  font: inherit;
  background: none; border: none; cursor: pointer; padding: 0;
  display: inline-flex; align-items: center; gap: 4px;
  max-width: 100%; min-width: 0;
  text-align: left;
  color: ${({ theme }) => theme.colors.secondary}; font-weight: 600;
  &:hover { text-decoration: underline; }
`

const DocName = styled.span`
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;
`

const DocMuted = styled.span`
  display: block;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: #94a3b8; font-size: 12px;
`

const Muted = styled.span` color: #b6b9c2; `

const RowActions = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end;
`

const RowBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  background: ${({ $primary, theme }) => ($primary ? theme.colors.secondary : '#fff')};
  color: ${({ $primary, theme }) => ($primary ? '#fff' : theme.colors.haiti)};
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.secondary : '#e4e4e7')};
  border-radius: 999px;
  min-height: 40px;
  padding: 9px 16px; font-size: 13px; font-weight: 700; cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { ${({ $primary }) => ($primary ? 'filter: brightness(1.06);' : 'border-color: #cbd5e1;')} }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const DismissedInline = styled.span`
  font-size: 11.5px; font-weight: 700; color: #9aa0ab; white-space: nowrap;
`

const UndoBtn = styled.button`
  font: inherit;
  background: none; border: none; cursor: pointer;
  font-size: 11.5px; font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  padding: 5px 4px; white-space: nowrap;
  &:hover:not(:disabled) { text-decoration: underline; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const FootCount = styled.div`
  text-align: center;
  font-size: 12px; font-weight: 600; color: #94a3b8;
  padding: 2px 0;
`

const ToastLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ToastUndo = styled.button`
  font: inherit;
  font-weight: 700;
  color: #1d7f8a;
  background: #fff;
  border: 1px solid #33becc;
  border-radius: 6px;
  min-height: 34px;
  padding: 0 12px;
  cursor: pointer;
`
